import { useState, useEffect } from "react";
import FormInput from "../../../components/controls/FormInput";
import FormTextarea from "../../../components/controls/FormTextArea";
import FormSelectBox from "../../../components/controls/FormSelectBox";
import ImageUploader from "../../../components/controls/ImageUploader";
import DateTimePicker from "../../../components/controls/DateTimePicker";

export default function ProviderNewsModal({
  isOpen,
  onClose,
  onSave,
  newsItem,
  userId,
}) {
  const isEditing = !!newsItem;

  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [publishDate, setPublishDate] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [tags, setTags] = useState([]);

  const safeFetch = async (url) => {
    try {
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
        },
      });

      if (!res.ok) return null;

      const txt = await res.text();
      if (!txt) return null;

      return JSON.parse(txt);
    } catch {
      return null;
    }
  };

  const loadCompanies = async () => {
    const data = await safeFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Company?userId=${userId}`
    );

    const mapped = Array.isArray(data)
      ? data.map((c) => ({ id: c.id, name: c.name }))
      : [];

    setCompanies(mapped);
  };

  const loadTags = async () => {
    const data = await safeFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`
    );

    if (!Array.isArray(data)) {
      setTags([]);
      return;
    }

    const mapped = data.map((t) => ({
      id: t.id,
      name: t.nameFr,
    }));

    setTags(mapped);
  };

  useEffect(() => {
    loadCompanies();
    loadTags();

    if (newsItem) {
      setTitle(newsItem.title || "");
      setImageUrl(newsItem.imageUrl || "");
      setText(newsItem.content || "");
      setTag(newsItem.tagId || "");
      setCompanyId(newsItem.companyId || 0);
      setPublishDate(
        newsItem.publishDate ? new Date(newsItem.publishDate) : null
      );
    } else {
      setTitle("");
      setImageUrl("");
      setText("");
      setTag("");
      setCompanyId(0);
      setPublishDate(null);
    }
  }, [newsItem]);

  const handleSave = async () => {
    if (!title.trim()) return alert("Veuillez entrer un titre.");
    if (!imageUrl.trim()) return alert("Veuillez ajouter une image.");
    if (!text.trim()) return alert("Veuillez entrer un texte.");
    if (!tag) return alert("Veuillez sélectionner un tag.");
    if (!companyId) return alert("Veuillez sélectionner une compagnie.");

    const payload = {
      id: newsItem?.id || null,
      title,
      imageUrl,
      text,
      tagId: tag,
      companyId: Number(companyId),
      publishDate: publishDate ? new Date(publishDate) : null,
      userId,
      status: "draft",
    };

    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/News`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": userId,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const message = await res.text();
      alert("Erreur : " + message);
      return;
    }

    onSave();
  };

  return (
    <dialog className={`modal ${isOpen ? "modal-open" : ""}`}>
      <div className="modal-box max-w-3xl">
        <h3 className="font-bold text-lg mb-4">
          {isEditing ? "Modifier l'actualité" : "Ajouter une actualité"}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Titre de l'article"
            name="title"
            value={title}
            onChange={(n, v) => setTitle(v)}
            required
            maxLength={100}
          />
          <DateTimePicker
            label="Date de publication"
            value={publishDate}
            onChange={setPublishDate}
          />

          <FormSelectBox
            label="Tag"
            name="tag"
            value={tag}
            onChange={(n, v) => setTag(v)}
            options={tags}
            required
            placeholder="Sélectionner un tag"
          />

          <FormSelectBox
            label="Compagnie"
            name="companyId"
            value={companyId}
            onChange={(n, v) => setCompanyId(parseInt(v, 10))}
            options={companies}
            required
            placeholder="Sélectionner une compagnie"
          />
        </div>

        <FormTextarea
          label="Texte"
          name="text"
          value={text}
          onChange={(n, v) => setText(v)}
          lines={6}
          required
          showMaxLength
          maxLength={10000}
        />
        <div className="my-4">
          <ImageUploader
            initialImageUrl={imageUrl}
            onTempImageChange={(url) => setImageUrl(url || "")}
            bucket="actualites"
          />
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Toutes les actualités seront validées par notre équipe avant
          publication.
        </p>

        <div className="modal-action mt-6">
          <button className="btn btn-outline rounded-full" onClick={onClose}>
            Annuler
          </button>
          <button
            className="btn bg-primary text-white rounded-full"
            onClick={handleSave}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </dialog>
  );
}
